import {useQuery} from '@tanstack/react-query';

import {useStores} from '../../stores/use-stores';
import apiService from '../../transport/apiService';
import {logger} from '../../utils/logger';

import {wishlistKeys} from './keys';
import {Wishlist, WishlistData, WishlistEntry} from './types';

/**
 * Query hook for fetching the user's wishlist and entries
 *
 * - Fetches existing wishlists (MFL supports multiple, we use one)
 * - Auto-creates a default wishlist if none exists
 * - Fetches entries (product_code/product_id pairs) for the wishlist
 *
 * @returns Query result with wishlist metadata and entries
 *
 * @example
 * const {data, isLoading, error} = useWishlist();
 * // data?.wishlist.id - the wishlist ID
 * // data?.entries - array of WishlistEntry
 */
export const useWishlist = () => {
  const {userStore} = useStores();

  return useQuery<WishlistData>({
    queryKey: wishlistKeys.list(),

    queryFn: async () => {
      // 1. Fetch existing wishlists
      let wishlists: Wishlist[] = [];
      try {
        wishlists = await apiService.getWishlists();
      } catch (error) {
        logger.error('useWishlist', 'Unable to load wishlists for user', {error});
        throw error;
      }

      // 2. Create default wishlist if none exists
      if (wishlists.length === 0) {
        try {
          const defaultList = await apiService.createWishlist('My wishlist');
          wishlists = [defaultList];
        } catch (error) {
          logger.error('useWishlist', 'Unable to create default wishlist for user', {error});
          throw error;
        }
      }

      //wishlists 3. Fetch entries for the first (only) wishlist
      let entries: WishlistEntry[] = [];
      try {
        entries = (await apiService.getItemsInWishlist(wishlists[0].id)) || [];
      } catch (error) {
        logger.error('useWishlist', 'Unable to get entries for wishlist', {
          wishlistId: wishlists[0].id,
          error,
        });
        // Return wishlist with empty entries rather than failing entirely
        entries = [];
      }

      return {
        wishlist: wishlists[0],
        entries,
      };
    },

    // Only fetch when user is logged in
    enabled: !!userStore.isLoggedIn,
  });
};
